import React, { useState, useEffect } from 'react';
import { Icon } from "@/components/ui/icon";
import { useLocation } from 'react-router-dom';
import { trackEvent } from '@/utils/analytics';

interface TelegramButtonProps {
  telegramHandle?: string;
}

const TelegramButton: React.FC<TelegramButtonProps> = ({ 
  telegramHandle = 'luckyman9099' 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();

  // Show button after component mounts with animation
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  // Hide on admin pages
  const shouldHide = location.pathname.startsWith('/admin');

  const handleTelegramClick = () => {
    const telegramUrl = `https://t.me/${telegramHandle}`;
    
    // Track click for analytics
    trackEvent('telegram_click', 'communication', location.pathname);
    
    // Open Telegram
    window.open(telegramUrl, '_blank', 'noopener,noreferrer');
  };

  // Don't render on admin pages or if not visible yet
  if (shouldHide || !isVisible) return null;

  return (
    <div className="fixed bottom-5 right-5 z-[9999]">
      {/* Telegram Button */}
      <button
        onClick={handleTelegramClick}
        className="
          w-[60px] h-[60px] sm:w-[50px] sm:h-[50px]
          bg-[#0088cc] hover:bg-[#006ba3] 
          rounded-full shadow-lg hover:shadow-xl
          flex items-center justify-center
          transition-all duration-300 ease-out
          hover:scale-110 active:scale-95
          focus:outline-none focus:ring-4 focus:ring-[#0088cc]/30
          group relative
          animate-[gentlePulse_3s_ease-in-out_infinite] hover:animate-none
        "
        style={{
          boxShadow: '0 4px 12px rgba(0, 136, 204, 0.3)',
          animation: `fadeInScale 0.5s ease-out, ${isVisible ? 'gentlePulse 3s ease-in-out infinite' : ''}`
        }}
        aria-label="Chat with us on Telegram"
        title="Chat with us on Telegram"
      >
        <Icon 
          name="chat"
          className="w-6 h-6 sm:w-5 sm:h-5 text-white group-hover:scale-110 transition-transform duration-200" 
        />
        
        {/* Tooltip */}
        <div className="
          absolute bottom-full right-0 mb-2 px-3 py-1
          bg-popover text-popover-foreground text-xs rounded-md
          opacity-0 group-hover:opacity-100
          transition-opacity duration-200
          whitespace-nowrap pointer-events-none
          before:content-[''] before:absolute before:top-full before:right-4
          before:border-4 before:border-transparent before:border-t-popover
        ">
          Chat with us on Telegram
        </div>
      </button>

      {/* Custom Styles */}
      <style>{`
        @keyframes fadeInScale {
          0% {
            opacity: 0;
            transform: scale(0.5) translateY(20px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        @keyframes gentlePulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }
      `}</style>
    </div>
  );
};

export default TelegramButton;